/**
 * Settings, Agent accounts (ADR 0071, ADR 0078): the accounts each coding agent's sessions run on
 * (Claude Code, Codex, Gemini CLI), saved once, and each project's default per agent. A sign-in
 * account is a folder per machine that the person signs in to once with the agent's own command;
 * keys are write-only and shown by their fingerprint.
 */
import { ChevronRight, FlaskConical, FolderPlus, KeyRound, LogIn, Plus, RotateCw, Trash2, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import {
  ACCOUNT_KINDS,
  LAUNCH_ACCOUNT_AGENTS,
  LAUNCH_ACCOUNT_AGENT_KINDS,
  LAUNCH_ACCOUNT_AGENT_LABEL,
  LAUNCH_ACCOUNT_FOLDERS_ENV,
  LAUNCH_ACCOUNT_SECRET_LABEL,
  LAUNCH_FOLDER_STATE_LABEL,
  LAUNCH_POOL_STRATEGY_LABEL,
  POOL_LAUNCH_ACCOUNT_ID,
  POOL_LAUNCH_ACCOUNT_LABEL,
  SECRET_MIN_LENGTH,
  accountLabel,
  cleanLaunchAccountSettings,
  isPerTokenLaunchAccount,
  launchAccountAgent,
  launchAccountKindLabel,
  launchAccountOwnFolder,
  machineSignInAccount,
  isDefaultLaunchAccountName,
  launchAccountSecretNeed,
  orderedFallbacks,
  launchAccountSummary,
  liveWindows,
  usageRefused,
  limitLabel,
  signInCommand,
} from '@orgus/protocol';
import type {
  AccountKind,
  AgentUsage,
  LaunchAccountTest,
  LaunchAccount,
  LaunchAccountAgent,
  LaunchAccountKind,
  LaunchAccountSettings,
  LaunchAccountsState,
  LaunchFallbackInput,
  LaunchFolderStatus,
  LaunchPoolStrategy,
  LaunchSignIn,
  Machine,
} from '@orgus/protocol';
import {
  Badge,
  Button,
  ConfirmDialog,
  CopyButton,
  Disclosure,
  Drawer,
  ErrorState,
  Checkbox,
  Input,
  Label,
  ListRow,
  Panel,
  RelativeTime,
  RowList,
  RowStatus,
  RowTitle,
  Segmented,
  Select,
  Skeleton,
  SkeletonRows,
  cn,
  plural,
  Switch,
  formatInt,
  formatMoney,
  formatMoneyCompact,
  formatPercent,
  formatRelative,
  Gauge,
  toUiError,
  toast,
  useNow,
} from '@orgus/ui';
import type { RowStatusProps, UiError } from '@orgus/ui';
import { useMachines, useOrgus, useProjects } from '../../lib/store.ts';
import { AgentGlyph } from '../adapters/AgentKind.tsx';
import { useCan } from '../auth/useMe.ts';
import { resetLabel } from '../scheduler/AgentUsageRows.tsx';
import { useSchedulerState } from '../scheduler/data.ts';
import { useCosts } from '../costs/useCosts.ts';
import { accountUsages, refreshUsage, rowWindows, testAccount, windowShort } from './data.ts';
import {
  accountLimit,
  accountsOf,
  capChoices,
  clearLimit,
  deleteLaunchAccount,
  foldersOf,
  machineSignInOf,
  machineSignInView,
  removeLaunchFolder,
  sendMachineSignInCode,
  startMachineSignIn,
  stopMachineSignIn,
  saveLaunchAccount,
  savePool,
  setLaunchFallback,
  setLaunchFolder,
  setProjectDefault,
  signInMachine,
  signInTermFor,
  signInView,
  startSignIn,
  tokenSignInView,
  useLaunchAccounts,
} from './data.ts';
import { closeTerminal, useTerms } from '../terminal/api.ts';
import { AgentTiles, ConnectDialog, MachineChips, MachineDots, MachineSignIn } from './Connect.tsx';
import { machineChips, wayLabel } from './connect.ts';
import { KindFields, draftError, draftOf, emptyDraft, settingsOf } from './fields.tsx';
import type { Draft } from './fields.tsx';
import { TerminalView } from '../terminal/TerminalView.tsx';
import { Glyph } from './Glyph.tsx';
import { PoolChannel, UnnamedLaunch } from './PoolChannel.tsx';
import type { PoolLaunch } from './PoolChannel.tsx';
import { POOL_DIVIDER, poolLanes, poolPlan, splitOrder } from './pool.ts';
import { ModuleAbsent } from '../server/ModuleAbsent.tsx';

/**
 * The agents Settings offers accounts for: every one. Both agents apply Codex and Gemini CLI
 * accounts (ADR 0078), and their subscriptions sign in on each machine from here (ADR 0108).
 */
const SHOWN_AGENTS: readonly LaunchAccountAgent[] = LAUNCH_ACCOUNT_AGENTS;

type Status = Pick<RowStatusProps, 'tone' | 'icon' | 'label'>;

/** An account key in words: 'Anthropic org 8e1d7c42', 'ChatGPT a41c09e2'; provider keys as they are. */
function shortKey(key: string): string {
  // A token sign-in whose organisation is unknown (ADR 0088): its own key.
  if (key.startsWith('anthropic:token-')) return `Claude token ${key.slice('anthropic:token-'.length)}`;
  const kind = key.slice(0, key.indexOf(':')) as AccountKind;
  return ACCOUNT_KINDS.includes(kind) && kind !== 'vertex' && kind !== 'foundry' && kind !== 'bedrock' ? accountLabel({ kind, key }) : key;
}

/** One status per account row: whether a session can run on it now; a limit the pool honours comes first (ADR 0088). */
function accountStatus(state: LaunchAccountsState, a: LaunchAccount, machines?: readonly { id: string; name: string; online: boolean }[]): Status {
  const limit = accountLimit(state, a.id);
  if (limit) return { tone: 'attention', icon: 'warning', label: limitLabel(limit) };
  // The last test failed (ADR 0088 decision 10): say so until a test passes.
  const test = state.tests?.[a.id];
  if (test && !test.ok) return { tone: 'danger', icon: 'failed', label: 'Test failed' };
  // A Claude subscription with the hub-held token runs on every machine (ADR 0088 amendment).
  if (a.kind === 'subscription' && a.secret) return { tone: 'neutral', icon: 'done', label: 'Signed in' };
  // A ChatGPT or Google sign-in (ADR 0108): ready, or how many machines still need it.
  if (machineSignInAccount(a) && machines) {
    const chips = machineChips(state, a, machines);
    const need = chips.filter((c) => c.state === 'needs-sign-in').length;
    if (chips.some((c) => c.state === 'signing-in')) return { tone: 'working', icon: 'working', label: 'Signing in' };
    if (need) return { tone: 'attention', icon: 'warning', label: need === chips.length ? 'Needs sign-in' : `Needs sign-in on ${formatInt(need)}` };
    return { tone: 'neutral', icon: 'done', label: 'Ready' };
  }
  if (a.kind === 'subscription') {
    const folders = foldersOf(state, a);
    if (!folders.length) return a.builtIn ? { tone: 'neutral', icon: 'queued', label: 'Checking' } : { tone: 'attention', icon: 'warning', label: 'No folder yet' };
    const signed = folders.filter((f) => f.status?.state === 'signed-in').length;
    if (signed === folders.length)
      return {
        tone: 'neutral',
        icon: 'done',
        label: folders.length === 1 ? 'Signed in' : `Signed in on ${signed} machines`,
      };
    if (signed > 0)
      return {
        tone: 'attention',
        icon: 'warning',
        label: `Signed in on ${signed} of ${folders.length} machines`,
      };
    return a.builtIn ? { tone: 'neutral', icon: 'idle', label: 'Not signed in' } : { tone: 'attention', icon: 'warning', label: 'Not signed in' };
  }
  if (launchAccountSecretNeed(a.kind) === 'required' && !a.secret) return { tone: 'attention', icon: 'warning', label: 'Key missing' };
  return { tone: 'neutral', icon: 'done', label: 'Ready' };
}

/** The dim line under an account's name: its kind and what tells it apart. */
function accountMeta(state: LaunchAccountsState, a: LaunchAccount): string {
  const agent = launchAccountAgent(a);
  if (a.builtIn) return "Each machine's own sign-in";
  const kind = a.kind === 'foundry' || a.kind === 'bedrock' || a.kind === 'vertex' ? launchAccountKindLabel(agent, a.kind) : wayLabel(a);
  if (a.kind === 'subscription') {
    const label = state.folders.find((f) => f.accountId === a.id && f.account?.label)?.account?.label;
    return [kind, label ?? (a.secret ? `token ${a.secret.fingerprint}` : '')].filter(Boolean).join(', ');
  }
  const detail = a.kind === 'api-key' ? (a.secret ? `key ${a.secret.fingerprint}` : '') : launchAccountSummary(a);
  return [kind, detail].filter(Boolean).join(', ');
}

function folderStatus(st: LaunchFolderStatus | null): Status {
  if (!st) return { tone: 'neutral', icon: 'queued', label: 'Checking' };
  if (st.state === 'signed-in')
    return {
      tone: 'neutral',
      icon: 'done',
      label: st.account?.label ? `Signed in, ${st.account.label}` : 'Signed in',
    };
  if (st.state === 'signed-out')
    return {
      tone: 'attention',
      icon: 'warning',
      label: LAUNCH_FOLDER_STATE_LABEL['signed-out'],
    };
  return {
    tone: 'danger',
    icon: 'failed',
    label: LAUNCH_FOLDER_STATE_LABEL[st.state],
  };
}

// ── Usage (ADR 0088) ─────────────────────────────────────────────────────────

/** This month's spend filed under an account's key (Spend, ADR 0067), or null when none is known. */
function useMonthSpend(a: Pick<LaunchAccount, 'accountKey'>): number | null {
  const costs = useCosts();
  if (!a.accountKey) return null;
  const b = costs?.byAccount?.find((x) => x.key === a.accountKey);
  return b ? b.cost : null;
}

/**
 * An account's usage at a glance, on its row: a ring per window (the current session, then the
 * fullest other window) with the percent used, or this month's spend for an account billed per use.
 */
function UsageAtAGlance({ a }: { a: LaunchAccount }) {
  const now = useNow(60_000);
  const { state } = useSchedulerState();
  const spend = useMonthSpend(a);
  const latest = accountUsages(state?.usage, a)[0] ?? null;
  const windows = rowWindows(latest, now);
  if (windows.length) {
    return (
      <span className="hidden items-center gap-2 sm:flex">
        {windows.map((w) => {
          const pct = formatPercent((w.percent ?? 0) / 100);
          return (
            <span key={`${w.kind}:${w.scope ?? ''}`} className="flex flex-col items-center" title={`${w.label}: ${pct} used${w.resetsAt ? `, resets ${resetLabel(w.resetsAt, now)}` : ''}`}>
              <Gauge variant="ring" size={32} value={(w.percent ?? 0) / 100} label={w.percent === null ? `${w.label}: percent not reported` : `${w.label}: ${pct} used`} valueText={w.percent === null ? '?' : Math.round(w.percent)} />
              <span className="text-caption text-fg-subtle">{windowShort(w)}</span>
            </span>
          );
        })}
      </span>
    );
  }
  if (a.kind !== 'subscription' && spend !== null) {
    return (
      <span className="hidden flex-col items-end sm:flex" title="Spend filed under this account this month">
        <span className="text-ui text-fg num">{formatMoneyCompact(spend)}</span>
        <span className="text-caption text-fg-subtle">this month</span>
      </span>
    );
  }
  return null;
}

/** A test result as one status: works, or failed with its cause. */
function testStatus(t: LaunchAccountTest): Status {
  return t.ok ? { tone: 'neutral', icon: 'done', label: 'Works' } : { tone: 'danger', icon: 'failed', label: 'Test failed' };
}

/**
 * Whether the account works (ADR 0088 decision 10): Test account runs one tiny request on it on a
 * machine, and the last answer stays here. A failed test keeps it out of the pool.
 */
function AccountCheck({ a, test }: { a: LaunchAccount; test: LaunchAccountTest | null }) {
  const machines = useMachines();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await testAccount(a.id);
    } catch (e) {
      setError(toUiError(e, { what: 'The account was not tested.' }));
    } finally {
      setBusy(false);
    }
  };
  const machine = test ? (machines.find((m) => m.id === test.machineId)?.name ?? null) : null;
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <h3 className="text-ui font-medium text-fg">Check</h3>
        <span className="ml-auto">
          <Button size="sm" variant="ghost" icon={<FlaskConical />} loading={busy} onClick={() => void run()}>
            Test account
          </Button>
        </span>
      </div>
      <div className="flex items-center gap-3 rounded-card bg-raised px-3 py-2">
        {busy ? (
          <RowTitle title="Testing" meta="One short request on this account" />
        ) : test ? (
          <RowTitle
            title={test.summary}
            meta={
              <>
                {[machine, test.model].filter(Boolean).join(', ')}
                {machine || test.model ? '. ' : ''}
                Tested <RelativeTime ts={test.at} />
              </>
            }
          />
        ) : (
          <RowTitle title="Not tested yet" meta="A test sends one short request on this account and reads its limits." />
        )}
        {!busy && test && <RowStatus {...testStatus(test)} hideLabel="below-lg" />}
        {busy && <RowStatus tone="working" icon="working" label="Testing" hideLabel="below-lg" />}
      </div>
      {error && <ErrorState variant="inline" error={error} />}
      {!busy && test && !test.ok && <p className="text-caption text-fg-muted">The pool passes it over until a test passes or its limit ends.</p>}
    </section>
  );
}

/** Test every account of the agent, one after another, so each has a fresh answer and fresh limits. */
function TestAllButton({ accounts }: { accounts: LaunchAccount[] }) {
  const [at, setAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setError(null);
    let failed = 0;
    for (let i = 0; i < accounts.length; i++) {
      setAt(i + 1);
      try {
        const t = await testAccount(accounts[i]!.id);
        if (!t.ok) failed++;
      } catch {
        failed++;
      }
    }
    setAt(null);
    if (failed) setError(`${plural(failed, 'account')} failed its test. Open it to see why.`);
  };
  return (
    <span className="flex items-center gap-2">
      {error && (
        <span className="hidden text-caption text-status-failed sm:inline" role="alert">
          {error}
        </span>
      )}
      <Button size="sm" variant="ghost" icon={<FlaskConical />} disabled={at !== null || !accounts.length} onClick={() => void go()} aria-label={at !== null ? `Testing ${at} of ${accounts.length}` : 'Test all accounts'}>
        <span className="hidden sm:inline">{at !== null ? `Testing ${at} of ${accounts.length}` : 'Test all'}</span>
      </Button>
    </span>
  );
}

/** Read every account's usage now; the title says when it was last read. */
function RefreshUsageButton({ state }: { state: LaunchAccountsState }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const now = useNow(30_000);
  const running = busy || (!!state.usageRefresh && state.usageRefresh.endedAt === null);
  const last = state.usageRefresh?.endedAt ?? null;
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await refreshUsage();
    } catch (e) {
      setError(toUiError(e, { what: 'Usage was not refreshed.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="flex items-center gap-2">
      {error && (
        <span className="text-caption text-status-failed" role="alert">
          {error.what} {error.why}
        </span>
      )}
      <Button
        size="sm"
        variant="ghost"
        icon={<RotateCw className={cn(running && 'animate-spin')} />}
        disabled={running}
        onClick={() => void go()}
        title={last ? `Usage read ${formatRelative(last, now)}` : 'Read every account usage now'}
        aria-label={running ? 'Reading usage' : 'Refresh usage'}
      >
        {/* Icon only on a phone: the panel title keeps its room. */}
        <span className="hidden sm:inline">{running ? 'Reading usage' : 'Refresh usage'}</span>
      </Button>
    </span>
  );
}

/** One reading as rings: each window's percent used, its name and when it resets; a refusal first. */
function WindowRings({ usage, machineName, now }: { usage: AgentUsage; machineName: string | null; now: number }) {
  const windows = liveWindows(usage, now);
  const refused = usageRefused(usage, now);
  if (!windows.length && !refused) return null;
  return (
    <div className="flex flex-col gap-2" aria-label={machineName ? `Usage on ${machineName}` : 'Usage'}>
      {machineName && <span className="text-caption text-fg-subtle">{machineName}</span>}
      {refused && (
        <p className="flex items-center gap-1 text-caption text-status-attention">
          <TriangleAlert className="size-3" aria-hidden />
          Usage limit reached
          {usage.resetsAt ? `, resets ${resetLabel(usage.resetsAt, now)}` : ''}
        </p>
      )}
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        {windows.map((w) => (
          <div key={`${w.kind}:${w.scope ?? ''}`} className="flex items-center gap-3">
            {w.percent !== null ? (
              <Gauge variant="ring" size={56} value={w.percent / 100} label={`${w.label}: ${formatPercent(w.percent / 100)} used`} valueText={`${Math.round(w.percent)}%`} />
            ) : (
              <span className="flex size-14 items-center justify-center rounded-full bg-active text-caption text-fg-subtle">n/a</span>
            )}
            <span className="flex flex-col">
              <span className="text-ui text-fg">{w.label}</span>
              <span className="text-caption text-fg-subtle">{w.resetsAt !== null ? `Resets ${resetLabel(w.resetsAt, now)}` : 'Reset not reported'}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The drawer's usage: every window as a ring with its reset, when it was read, and Refresh. */
function AccountUsage({ a }: { a: LaunchAccount }) {
  const now = useNow(30_000);
  const machines = useMachines();
  const { state } = useSchedulerState();
  const { state: accounts } = useLaunchAccounts();
  const spend = useMonthSpend(a);
  const usages = accountUsages(state?.usage, a);
  const perUse = a.kind !== 'subscription';
  if (a.agent !== 'claude-code' && !usages.length && spend === null) return null;
  const shown = a.builtIn ? usages : usages.slice(0, 1);
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <h3 className="text-ui font-medium text-fg">Usage</h3>
        {usages[0] && <span className="text-caption text-fg-subtle">Read {formatRelative(usages[0].at, now)}</span>}
        <span className="ml-auto">{accounts && !perUse && <RefreshUsageButton state={accounts} />}</span>
      </div>
      <div className="flex flex-col gap-3 rounded-card bg-raised px-3 py-3">
        {shown.length ? (
          shown.map((u) => <WindowRings key={`${u.machineId}:${u.accountId ?? ''}`} usage={u} machineName={a.builtIn && shown.length > 1 ? (machines.find((m) => m.id === u.machineId)?.name ?? null) : null} now={now} />)
        ) : (
          <p className="text-caption text-fg-muted">{perUse ? 'Billed per use: it has no usage windows.' : 'Not read yet. Refresh usage reads it now, and every session on this account reports it too.'}</p>
        )}
        {spend !== null && (
          <p className="text-caption text-fg-muted">
            Spend this month <span className="text-fg num">{formatMoney(spend)}</span>
          </p>
        )}
      </div>
    </section>
  );
}

// ── Sign in with a token (ADR 0088 amendment) ────────────────────────────────

type OpenedSignIn = { machineId: string; termId: string };

/**
 * A Claude subscription's sign-in: claude setup-token in a console terminal on one machine; the hub
 * keeps the token, so every machine runs the account. Shows the pane while it runs.
 */
function TokenSignIn({ a, opened, onOpened }: { a: LaunchAccount; opened: OpenedSignIn | null; onOpened: (s: OpenedSignIn | null) => void }) {
  const machines = useMachines();
  const { terms } = useTerms();
  const online = machines.filter((m) => m.online);
  const [machineId, setMachineId] = useState<string>('');
  const [busy, setBusy] = useState<'sign-in' | 'close' | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const chosen = machineId || signInMachine(machines) || '';
  const term = opened ? terms.find((t) => t.id === opened.termId) : undefined;
  const view = tokenSignInView(a, term);
  const live = !!term && term.state !== 'exited';
  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>, what: string) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(toUiError(e, { what }));
    } finally {
      setBusy(null);
    }
  };
  const signIn = () =>
    run(
      'sign-in',
      async () =>
        onOpened({
          machineId: chosen,
          termId: await startSignIn(a.id, chosen),
        }),
      'The sign-in did not start.',
    );
  const close = () =>
    run(
      'close',
      async () => {
        if (term && term.state !== 'exited') await closeTerminal(term.id);
        onOpened(null);
      },
      'The terminal was not closed.',
    );
  const st: Status = live && !a.secret ? { tone: 'working', icon: 'working', label: 'Signing in' } : a.secret ? { tone: 'neutral', icon: 'done', label: 'Signed in on every machine' } : { tone: 'attention', icon: 'warning', label: 'Not signed in' };
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-ui font-medium text-fg">Sign-in</h3>
      <div className="flex flex-col gap-2 rounded-card bg-raised px-3 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <RowTitle
            title={a.secret ? `Token ${a.secret.fingerprint}` : 'Sign in once, in your browser'}
            meta={
              a.secret ? (
                <>
                  Set <RelativeTime ts={a.secret.setAt} />. Every Orgus session on this account uses it.
                </>
              ) : (
                'Orgus keeps the token and uses it on every machine.'
              )
            }
          />
          <RowStatus {...st} hideLabel="below-lg" />
          <span className="flex shrink-0 items-center gap-1">
            {view.signIn && online.length > 1 && <Select aria-label="Machine to sign in on" size="sm" wrapperClassName="w-36" value={chosen} onValueChange={setMachineId} options={online.map((m) => ({ value: m.id, label: m.name }))} />}
            {view.signIn && (
              <Button size="sm" variant={a.secret ? 'ghost' : 'primary'} icon={<LogIn />} loading={busy === 'sign-in'} disabled={!chosen} onClick={() => void signIn()}>
                {a.secret ? 'Sign in again' : 'Sign in'}
              </Button>
            )}
            {view.close && (
              <Button size="sm" variant="ghost" loading={busy === 'close'} onClick={() => void close()}>
                {live && !a.secret ? 'Stop sign-in' : 'Close terminal'}
              </Button>
            )}
          </span>
        </div>
        {live && !a.secret && <p className="text-caption text-fg-subtle">Approve in your browser, then paste the code it shows into the terminal.</p>}
        {term && view.pane && <TerminalView termId={term.id} compact className="w-full" style={{ height: 'min(20rem, 50dvh)' }} />}
        {!online.length && !a.secret && <p className="text-caption text-fg-subtle">No machine is online to sign in on.</p>}
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </section>
  );
}

// ── Fallback: takes over when a subscription runs out (ADR 0071 decision 9) ─────

const ORDINAL = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];
const ordinal = (n: number) => ORDINAL[n - 1] ?? `Number ${formatInt(n)}`;

/**
 * A per-token account's switch "Use when a subscription runs out". It takes effect at once (it is
 * not part of Save account); scope, order and cap show only while it is on. Only an owner or admin
 * turns it on or changes it; anyone who may edit accounts may turn it off.
 */
function FallbackSection({ state, a }: { state: LaunchAccountsState; a: LaunchAccount }) {
  const projects = useProjects();
  const admin = useCan('admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const f = a.fallback ?? null;
  const count = orderedFallbacks(state.accounts).length;
  const run = async (input: LaunchFallbackInput) => {
    setBusy(true);
    setError(null);
    try {
      await setLaunchFallback(a.id, input);
    } catch (e) {
      setError(
        toUiError(e, {
          what: input.on ? 'The fallback was not saved.' : 'The fallback was not turned off.',
        }),
      );
    } finally {
      setBusy(false);
    }
  };
  const chosen = f?.projects ?? null;
  const toggleProject = (id: string, on: boolean) => {
    const next = on ? [...(chosen ?? []), id] : (chosen ?? []).filter((p) => p !== id);
    if (next.length) void run({ on: true, projects: next });
  };
  return (
    <section className="flex flex-col gap-4" aria-label="Fallback">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col">
          <Label htmlFor={`fallback-${a.id}`} className="text-ui text-fg">
            Use when a subscription runs out
          </Label>
          <p className="text-caption text-fg-subtle">{admin || f ? 'Queued work continues here, billed per token.' : 'Only an owner or admin can turn this on.'}</p>
        </div>
        <Switch id={`fallback-${a.id}`} checked={!!f} disabled={busy || (!f && !admin)} onCheckedChange={(on) => void run({ on })} />
      </div>
      {f && (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Select
              label="Projects"
              size="sm"
              disabled={!admin || busy}
              value={chosen ? 'chosen' : 'all'}
              onValueChange={(v) =>
                void run({
                  on: true,
                  projects: v === 'all' ? null : projects.slice(0, 1).map((p) => p.id),
                })
              }
              options={[
                { value: 'all', label: 'All projects' },
                {
                  value: 'chosen',
                  label: 'Chosen projects',
                  disabled: !projects.length,
                },
              ]}
            />
            {count > 1 && (
              <Select
                label="Order"
                size="sm"
                disabled={!admin || busy}
                value={String(f.order)}
                onValueChange={(v) => void run({ on: true, order: Number(v) })}
                options={Array.from({ length: count }, (_, i) => ({
                  value: String(i + 1),
                  label: ordinal(i + 1),
                }))}
              />
            )}
            <Select
              label="Daily cap"
              size="sm"
              disabled={!admin || busy}
              value={f.dailyCapUsd == null ? 'none' : String(f.dailyCapUsd)}
              onValueChange={(v) =>
                void run({
                  on: true,
                  dailyCapUsd: v === 'none' ? null : Number(v),
                })
              }
              options={[
                { value: 'none', label: 'No cap' },
                ...capChoices(f.dailyCapUsd).map((c) => ({
                  value: String(c),
                  label: `${formatMoneyCompact(c)} a day`,
                })),
              ]}
            />
          </div>
          {chosen && (
            <ul className="flex flex-col gap-2" aria-label="Projects it serves">
              {projects.map((p) => (
                <li key={p.id}>
                  <Checkbox label={p.name} checked={chosen.includes(p.id)} disabled={!admin || busy || (chosen.length === 1 && chosen.includes(p.id))} onCheckedChange={(on) => toggleProject(p.id, on)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && <ErrorState variant="inline" error={error} />}
    </section>
  );
}

// ── ChatGPT and Google: sign in on the machine from here (ADR 0108) ──────────

/** What the person does next on a running sign-in: open the link, enter or paste the code. */
function MachineSignInSteps({ a, machineId, s }: { a: LaunchAccount; machineId: string; s: LaunchSignIn }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await sendMachineSignInCode(a.id, machineId, code.trim());
      setCode('');
    } catch (e) {
      setError(toUiError(e, { what: 'The code was not sent.' }));
    } finally {
      setBusy(false);
    }
  };
  if (s.step === 'failed')
    return (
      <ErrorState
        variant="inline"
        error={{
          what: 'The sign-in did not finish.',
          why: s.error ?? 'The machine stopped it.',
          next: 'Sign in again.',
        }}
      />
    );
  if (s.step === 'starting') return <p className="text-caption text-fg-subtle">{s.url ? 'Checking the code.' : 'Starting the sign-in on the machine.'}</p>;
  if (s.step !== 'open' && s.step !== 'paste') return null;
  const link = s.url ? (
    <a className="min-w-0 truncate text-accent-text underline-offset-2 hover:underline" href={s.url} target="_blank" rel="noopener noreferrer nofollow">
      {s.step === 'open' ? s.url.replace(/^https:\/\//, '') : 'Open Google sign-in'}
    </a>
  ) : null;
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-control bg-surface px-3 py-2">
      {s.step === 'open' ? (
        <>
          <p className="flex min-w-0 items-center gap-2 text-caption text-fg-muted">
            <span className="shrink-0">1. Open</span> {link}
          </p>
          <div className="flex items-center gap-2 text-caption text-fg-muted">
            <span>2. Enter</span>
            <code className="font-mono text-body font-medium text-fg">{s.code}</code>
            {s.code && <CopyButton text={s.code} label="Copy code" />}
          </div>
          <p className="text-caption text-fg-subtle">Turn on device code login in ChatGPT, Settings, Security first.</p>
        </>
      ) : (
        <>
          <p className="flex min-w-0 items-center gap-2 text-caption text-fg-muted">
            <span className="shrink-0">1.</span> {link}
          </p>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.trim()) void send();
            }}
          >
            <span className="shrink-0 text-caption text-fg-muted">2.</span>
            <Input aria-label="Code from Google" size="sm" wrapperClassName="min-w-0 flex-1" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste the code Google shows" spellCheck={false} autoComplete="off" className="font-mono" />
            <Button size="sm" variant="primary" type="submit" loading={busy} disabled={!code.trim()}>
              Send code
            </Button>
          </form>
        </>
      )}
      {error && <ErrorState variant="inline" error={error} />}
    </div>
  );
}

// ── One account: details and folders ─────────────────────────────────────────

const FOLDER_PLACEHOLDER: Record<LaunchAccountAgent, string> = {
  'claude-code': '.claude-max',
  codex: '.codex-pro',
  'gemini-cli': 'gemini-pro',
};

function FolderRow({
  a,
  machine,
  path,
  status,
  readOnly,
  machineSignIn = null,
  noSignIn = false,
}: {
  a: LaunchAccount;
  machine: Machine | undefined;
  path: string | null;
  status: LaunchFolderStatus | null;
  readOnly: boolean;
  machineSignIn?: LaunchSignIn | null;
  noSignIn?: boolean;
}) {
  const agent = launchAccountAgent(a);
  const machineId = machine?.id ?? status?.machineId ?? '';
  // A ChatGPT or Google subscription signs in on the machine from here (ADR 0108).
  const onMachine = !readOnly && !noSignIn && machineSignInAccount(a);
  const mv = machineSignInView(machineSignIn, path ? (status?.state ?? null) : null, !!machine?.online);
  const [typing, setTyping] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState<'create' | 'save' | 'forget' | 'sign-in' | 'close' | 'machine-sign-in' | 'stop' | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [fallback, setFallback] = useState(false);
  // The sign-in terminal this row opened (ADR 0071): its pane shows under the row until the folder is signed in.
  const [termId, setTermId] = useState<string | null>(null);
  const [hidden, setHidden] = useState<string | null>(null);
  const { terms } = useTerms();
  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>, what: string) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
      setTyping(false);
    } catch (e) {
      setError(toUiError(e, { what }));
    } finally {
      setBusy(null);
    }
  };
  // Folders sign in from the person's own terminal; the console's sign-in is the token one (TokenSignIn, ADR 0088 amendment).
  const consoleSignIn = false;
  const found = !readOnly && consoleSignIn && path && machineId ? signInTermFor(terms, machineId, path, termId) : undefined;
  const term = found && found.id !== hidden ? found : undefined;
  const view = signInView(path ? (status?.state ?? null) : 'missing', term);
  const live = !!term && term.state !== 'exited';
  const st: Status | null =
    onMachine && mv.running
      ? { tone: 'working', icon: 'working', label: 'Signing in' }
      : path
        ? live && status?.state !== 'signed-in'
          ? { tone: 'working', icon: 'working', label: 'Signing in' }
          : folderStatus(status)
        : onMachine
          ? { tone: 'attention', icon: 'warning', label: 'Not signed in' }
          : null;
  const machineSignInNow = () => run('machine-sign-in', () => startMachineSignIn(a.id, machineId), 'The sign-in did not start.');
  const stopMachine = () => run('stop', () => stopMachineSignIn(a.id, machineId), 'The sign-in was not stopped.');
  const command = path && !live && !mv.running && status?.state !== 'signed-in' && status?.state !== 'not-allowed' ? signInCommand(path, machine?.os ?? 'linux', agent) : null;
  const ph = FOLDER_PLACEHOLDER[agent];
  const signIn = () =>
    run(
      'sign-in',
      async () => {
        setHidden(null);
        setTermId(await startSignIn(a.id, machineId));
      },
      'The sign-in did not start.',
    );
  const close = () =>
    run(
      'close',
      async () => {
        if (term && term.state !== 'exited') await closeTerminal(term.id);
        setHidden(term?.id ?? null);
        setTermId(null);
      },
      'The terminal was not closed.',
    );
  return (
    <li className="flex flex-col gap-2 px-3 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <RowTitle title={machine?.name ?? machineId} meta={path ?? (machine && !machine.online ? (onMachine ? 'The machine is offline' : 'No folder yet. The machine is offline') : onMachine ? 'Sign in once on this machine' : 'No folder on this machine')} />
        {st && <RowStatus {...st} hideLabel="below-lg" />}
        {!readOnly && (
          <span className="flex shrink-0 gap-1">
            {onMachine && (mv.signIn || mv.again) && (
              <Button size="sm" variant={mv.again ? 'ghost' : 'primary'} icon={<LogIn />} loading={busy === 'machine-sign-in'} onClick={() => void machineSignInNow()}>
                {mv.again ? 'Sign in again' : 'Sign in'}
              </Button>
            )}
            {onMachine && (mv.running || machineSignIn?.step === 'failed') && (
              <Button size="sm" variant="ghost" loading={busy === 'stop'} onClick={() => void stopMachine()}>
                {mv.running ? 'Stop sign-in' : 'Dismiss'}
              </Button>
            )}
            {!onMachine && !path && !typing && (
              <>
                <Button size="sm" variant="secondary" icon={<FolderPlus />} loading={busy === 'create'} disabled={!machine?.online} onClick={() => void run('create', () => setLaunchFolder(a.id, machineId, { create: true }), 'The folder was not created.')}>
                  Create folder
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setTyping(true)}>
                  Use a folder
                </Button>
              </>
            )}
            {path && view.signIn && consoleSignIn && (
              <Button size="sm" variant="primary" icon={<LogIn />} loading={busy === 'sign-in'} disabled={!machine?.online} onClick={() => void signIn()}>
                Sign in
              </Button>
            )}
            {view.close && (
              <Button size="sm" variant="ghost" loading={busy === 'close'} onClick={() => void close()}>
                {live && status?.state !== 'signed-in' ? 'Stop sign-in' : 'Close terminal'}
              </Button>
            )}
            {path && !view.pane && !mv.running && (
              <Button size="sm" variant="ghost" loading={busy === 'forget'} onClick={() => void run('forget', () => removeLaunchFolder(a.id, machineId), 'The folder was not removed.')}>
                Forget folder
              </Button>
            )}
          </span>
        )}
      </div>
      {term && view.pane && <TerminalView termId={term.id} compact className="w-full" style={{ height: 'min(20rem, 50dvh)' }} />}
      {onMachine && machineSignIn && machineSignIn.step !== 'done' && <MachineSignInSteps a={a} machineId={machineId} s={machineSignIn} />}
      {typing && (
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run('save', () => setLaunchFolder(a.id, machineId, { path: value.trim() }), 'The folder was not saved.');
          }}
        >
          <Input
            aria-label={`Folder on ${machine?.name ?? 'the machine'}`}
            size="sm"
            wrapperClassName="min-w-0 flex-1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={machine?.os === 'windows' ? `C:\\Users\\you\\${ph}` : `/home/you/${ph}`}
            spellCheck={false}
            className="font-mono"
            autoFocus
          />
          <Button size="sm" variant="secondary" type="submit" loading={busy === 'save'} disabled={!value.trim()}>
            Use this folder
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setTyping(false)}>
            Cancel
          </Button>
        </form>
      )}
      {command && !view.pane && (
        <div className="flex min-w-0 flex-col gap-1">
          <Button size="sm" variant="ghost" className="self-start text-fg-subtle" icon={<ChevronRight className={cn(fallback && 'rotate-90')} />} aria-expanded={fallback} onClick={() => setFallback((x) => !x)}>
            Sign in from your own terminal
          </Button>
          {fallback && (
            <div className="flex min-w-0 items-center gap-2 rounded-control bg-surface px-3 py-2">
              <code className="min-w-0 flex-1 truncate font-mono text-caption text-fg-muted" title={command}>
                {command}
              </code>
              <CopyButton text={command} label="Copy sign-in command" />
            </div>
          )}
        </div>
      )}
      {command && agent === 'gemini-cli' && fallback && <p className="text-caption text-fg-subtle">Then choose Sign in with Google.</p>}
      {status?.state === 'not-allowed' && <p className="text-caption text-fg-muted">The machine runs sessions only in folders Orgus creates or that its owner lists in {LAUNCH_ACCOUNT_FOLDERS_ENV[agent]}. Create a folder instead.</p>}
      {error && <ErrorState variant="inline" error={error} />}
    </li>
  );
}

/**
 * The machines of a ChatGPT or Google sign-in: a chip per machine, and the sign-in steps of the
 * chosen one inline (the one the drawer opened for, else the first that needs a sign-in).
 */
function MachinesSection({ state, a, machineId }: { state: LaunchAccountsState; a: LaunchAccount; machineId: string | null }) {
  const machines = useMachines();
  const write = useCan('operator');
  const chips = machineChips(state, a, machines);
  const [picked, setPicked] = useState<string | null>(machineId);
  useEffect(() => setPicked(machineId), [machineId, a.id]);
  const target = picked ?? chips.find((c) => c.state === 'needs-sign-in' || c.state === 'signing-in')?.machineId ?? null;
  const chip = chips.find((c) => c.machineId === target);
  return (
    <section className="flex flex-col gap-3" aria-label="Machines">
      <div className="flex items-center gap-3">
        <h3 className="min-w-0 flex-1 text-ui font-medium text-fg">Machines</h3>
        <span className="text-caption text-fg-subtle">Each machine signs in once</span>
      </div>
      {chips.length ? <MachineChips chips={chips} onPick={write ? (c) => setPicked(c.machineId) : undefined} /> : <p className="text-caption text-fg-muted">No machine is connected yet.</p>}
      {write && chip && chip.state !== 'offline' && (
        <div className="rounded-card bg-raised p-4">
          <MachineSignIn key={chip.machineId} state={state} a={a} machineId={chip.machineId} machineName={chip.name} signedIn={chip.state === 'ready'} />
        </div>
      )}
      {chip?.state === 'offline' && <p className="text-caption text-fg-muted">{chip.name} is offline. Sign in there once it is back.</p>}
    </section>
  );
}

function AccountDrawer({
  state,
  account,
  machineId = null,
  signIn,
  onSignIn,
  onClose,
}: {
  state: LaunchAccountsState;
  account: LaunchAccount | null;
  machineId?: string | null;
  signIn: OpenedSignIn | null;
  onSignIn: (s: OpenedSignIn | null) => void;
  onClose: () => void;
}) {
  const machines = useMachines();
  const [d, setD] = useState<Draft>(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [removing, setRemoving] = useState(false);
  const [clearing, setClearing] = useState(false);
  useEffect(() => {
    if (!account) return;
    setD(draftOf(account));
    setError(null);
    setBusy(false);
    // Only when another account opens: live updates must not reset what the person typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);
  // The hub renamed the account (named after its sign-in, ADR 0088): follow it while the person has
  // not typed a name of their own, so Save never puts the old name back.
  const shownName = useRef(account?.name ?? '');
  useEffect(() => {
    if (!account) return;
    const was = shownName.current;
    shownName.current = account.name;
    if (was !== account.name) setD((x) => (x.name.trim() === was ? { ...x, name: account.name } : x));
  }, [account?.name]);
  if (!account) return <Drawer open={false} onOpenChange={() => onClose()} title="Account" />;
  const a = account;
  const agent = launchAccountAgent(a);
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const need = launchAccountSecretNeed(a.kind, a.agent);
  const tokenSignIn = !a.builtIn && agent === 'claude-code' && a.kind === 'subscription';
  const dirty = !a.builtIn && (d.name.trim() !== a.name || JSON.stringify(settingsOf(d)) !== JSON.stringify(a.settings) || !!d.secret.trim());

  const save = async () => {
    const problem = draftError(d, a);
    if (problem)
      return setError({
        what: 'The account was not saved.',
        why: problem,
        next: 'Fix it and save again.',
      });
    setBusy(true);
    setError(null);
    try {
      await saveLaunchAccount(
        {
          name: d.name.trim(),
          settings: settingsOf(d),
          ...(d.secret.trim() ? { secret: d.secret.trim() } : {}),
        },
        a.id,
      );
      set({ secret: '' });
      toast.success('Account saved');
    } catch (e) {
      setError(toUiError(e, { what: 'The account was not saved.' }));
    } finally {
      setBusy(false);
    }
  };

  const folderRows = a.builtIn
    ? foldersOf(state, a).map((f) => ({
        machine: machines.find((m) => m.id === f.machineId),
        path: f.path,
        status: f.status,
        key: f.machineId,
      }))
    : machines.map((m) => {
        const path = a.folders[m.id] ?? null;
        return {
          machine: m,
          path,
          status: path ? (state.folders.find((f) => f.accountId === a.id && f.machineId === m.id && f.path === path) ?? null) : null,
          key: m.id,
        };
      });
  const usedBy = Object.values(state.defaults).filter((x) => x[agent] === a.id).length;
  const agentName = LAUNCH_ACCOUNT_AGENT_LABEL[agent];
  const limit = accountLimit(state, a.id);
  const clear = async () => {
    setClearing(true);
    setError(null);
    try {
      await clearLimit(a.id);
    } catch (e) {
      setError(toUiError(e, { what: 'The limit was not cleared.' }));
    } finally {
      setClearing(false);
    }
  };

  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      eyebrow={a.builtIn ? `${agentName}, built in` : `${agentName}, ${launchAccountKindLabel(agent, a.kind)}`}
      title={a.name}
      description={a.accountKey ? `Filed in Spend as ${shortKey(a.accountKey)}` : a.kind === 'subscription' ? 'Spend learns the account once it is signed in' : undefined}
      width={640}
      resizeKey={null}
      bodyClassName="flex flex-col gap-6 px-6 py-4"
      actions={
        !a.builtIn ? (
          <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setRemoving(true)}>
            Delete account
          </Button>
        ) : undefined
      }
      footer={
        !a.builtIn && (
          <Button variant="primary" className="ml-auto" loading={busy} disabled={!dirty} onClick={() => void save()}>
            Save account
          </Button>
        )
      }
    >
      {!a.builtIn && (
        <section className="flex flex-col gap-4">
          <Input
            label="Name"
            value={d.name}
            onChange={(e) => set({ name: e.target.value })}
            maxLength={60}
            // Signed in under the name Orgus gave it: ask for one the person will recognise (ADR 0088).
            autoFocus={!!a.secret && isDefaultLaunchAccountName(a.name, agent, a.kind)}
            hint={a.secret && isDefaultLaunchAccountName(a.name, agent, a.kind) ? 'Signed in. Give it a name you will recognise.' : undefined}
          />
          <KindFields d={d} set={set} />
          {need !== 'none' && !tokenSignIn && (
            <Input
              label={a.secret ? 'Replace key' : LAUNCH_ACCOUNT_SECRET_LABEL[a.kind]}
              type="password"
              autoComplete="new-password"
              value={d.secret}
              onChange={(e) => set({ secret: e.target.value })}
              placeholder={a.secret ? `Stored key ${a.secret.fingerprint}` : 'Paste the key'}
              hint={
                a.secret ? (
                  <>
                    Set by {a.secret.setBy} <RelativeTime ts={a.secret.setAt} />
                  </>
                ) : (
                  'Stored encrypted, never shown again.'
                )
              }
              spellCheck={false}
              className="font-mono"
            />
          )}
          {error && <ErrorState variant="inline" error={error} />}
        </section>
      )}
      {limit && (
        <section className="flex items-center gap-3 rounded-card bg-raised px-3 py-2">
          <p className="min-w-0 flex-1 text-caption text-fg-muted">
            <span className="text-fg">{limitLabel(limit)}.</span> {limit.summary} The pool passes it over until then.
          </p>
          <Button size="sm" variant="ghost" loading={clearing} onClick={() => void clear()}>
            Clear limit
          </Button>
        </section>
      )}
      {machineSignInAccount(a) && <MachinesSection state={state} a={a} machineId={machineId} />}
      {tokenSignIn && <TokenSignIn a={a} opened={signIn} onOpened={onSignIn} />}
      <AccountUsage a={a} />
      {agent === 'claude-code' && <AccountCheck a={a} test={state.tests?.[a.id] ?? null} />}
      {a.builtIn && a.kind === 'subscription' && (
        <section className="flex flex-col gap-2">
          <h3 className="text-ui font-medium text-fg">Sign-in on each machine</h3>
          {folderRows.length ? (
            <ul className="flex flex-col gap-1 rounded-card bg-raised py-1" aria-label="Folders">
              {folderRows.map((f) => (
                <FolderRow key={f.key} a={a} machine={f.machine} path={f.path} status={f.status} readOnly machineSignIn={null} />
              ))}
            </ul>
          ) : (
            <p className="text-caption text-fg-muted">No machine has reported its sign-in yet.</p>
          )}
        </section>
      )}
      {!a.builtIn && (isPerTokenLaunchAccount(a) || a.kind === 'subscription') && (
        <Disclosure title="Advanced" summary={isPerTokenLaunchAccount(a) ? (a.fallback ? 'Fallback on' : 'Fallback off') : tokenSignIn ? 'Sign-in folders' : 'Folders and terminal sign-in'} className="-mx-2">
          <div className="flex flex-col gap-4">
            {isPerTokenLaunchAccount(a) && <FallbackSection state={state} a={a} />}
            {a.kind === 'subscription' && (
              <section className="flex flex-col gap-2">
                <p className="text-caption text-fg-subtle">{tokenSignIn ? 'Instead of the token, a folder per machine that you sign in to yourself.' : 'The folder each machine keeps this sign-in in, and the command to sign in from your own terminal.'}</p>
                <ul className="flex flex-col gap-1 rounded-card bg-raised py-1" aria-label="Folders">
                  {folderRows.map((f) => (
                    <FolderRow key={f.key} a={a} machine={f.machine} path={f.path} status={f.status} readOnly={false} machineSignIn={null} noSignIn />
                  ))}
                </ul>
              </section>
            )}
          </div>
        </Disclosure>
      )}
      <p className="text-caption text-fg-subtle">
        {usedBy ? `The ${agentName} default of ${plural(usedBy, 'project')}. ` : ''}
        {launchAccountOwnFolder({ agent, kind: a.kind }) ? 'Runs in a folder Orgus keeps for it on each machine. ' : ''}
        {a.kind === 'subscription' ? 'Orgus never switches a session to another subscription to get around usage limits.' : ''}
      </p>
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title={`Delete ${a.name}?`}
        description={`New sessions can no longer run on it${usedBy ? `, and ${usedBy === 1 ? 'the project that uses it as default goes' : `the ${usedBy} projects that use it as default go`} back to no default` : ''}. ${a.secret ? 'Its key is removed from the hub. ' : ''}Folders on the machines stay as they are.`}
        confirmLabel="Delete account"
        onConfirm={async () => {
          await deleteLaunchAccount(a.id);
          toast('Account deleted', { tone: 'neutral' });
          onClose();
        }}
      />
    </Drawer>
  );
}

// ── The pool (ADR 0088) ──────────────────────────────────────────────────────

/** The pool row's one status: off, or how many accounts and how it picks. */
function poolStatus(state: LaunchAccountsState, agent: LaunchAccountAgent): Status {
  const p = state.pools?.[agent];
  if (!p) return { tone: 'neutral', icon: 'idle', label: 'Off' };
  return {
    tone: 'neutral',
    icon: 'done',
    label: `${plural(p.members.length, 'account')}, ${LAUNCH_POOL_STRATEGY_LABEL[p.strategy].toLowerCase()}`,
  };
}

const STRATEGY_OPTIONS: { value: LaunchPoolStrategy; label: string }[] = [
  { value: 'in-order', label: LAUNCH_POOL_STRATEGY_LABEL['in-order'] },
  { value: 'spread', label: LAUNCH_POOL_STRATEGY_LABEL.spread },
];

/** How often the demo sends a launch into the pool, so the picture moves (calm: one every few seconds). */
const DEMO_LAUNCH_MS = 6_500;

/** Which accounts are in the pool, in what order, how Orgus picks, and whether the pool stands in, drawn as the pool itself. Applied on Save. */
function PoolDrawer({ state, agent, onClose, onAdd }: { state: LaunchAccountsState; agent: LaunchAccountAgent | null; onClose: () => void; onAdd: (agent: LaunchAccountAgent) => void }) {
  if (!agent) return <Drawer open={false} onOpenChange={() => onClose()} title="Pool" />;
  return <PoolDrawerOpen key={agent} state={state} agent={agent} onClose={onClose} onAdd={() => onAdd(agent)} />;
}

function PoolDrawerOpen({ state, agent, onClose, onAdd }: { state: LaunchAccountsState; agent: LaunchAccountAgent; onClose: () => void; onAdd: () => void }) {
  const accounts = accountsOf(state, agent);
  const prev = state.pools?.[agent];
  // The sortable order: the pool's members, the divider, then the accounts outside it. Live
  // updates never undo what the person set: it is read once, when the drawer opens.
  const [order, setOrder] = useState<string[]>(() => {
    const members = (prev?.members ?? []).filter((id) => accounts.some((a) => a.id === id));
    return [...members, POOL_DIVIDER, ...accounts.filter((a) => !members.includes(a.id)).map((a) => a.id)];
  });
  const [strategy, setStrategy] = useState<LaunchPoolStrategy>(prev?.strategy ?? 'in-order');
  const [whenUnset, setWhenUnset] = useState(prev?.whenUnset ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [launch, setLaunch] = useState<PoolLaunch>({ seq: 0, to: null });
  const mock = useOrgus((s) => s.mode === 'mock');
  const { state: scheduler, phase } = useSchedulerState();
  const now = useNow(30_000);
  // The demo's launches; a real hub's arrive as launchedAt changes below. Off the demo the clock idles hourly.
  const tick = useNow(mock ? DEMO_LAUNCH_MS : 3_600_000);
  const first = useRef(tick);
  useEffect(() => {
    if (!mock || tick === first.current) return;
    setLaunch((l) => ({ seq: l.seq + 1, to: null }));
  }, [mock, tick]);
  // A real launch from the pool: the account it ran on moved its launchedAt.
  const seenLaunch = useRef(state.launchedAt ?? {});
  useEffect(() => {
    const was = seenLaunch.current;
    const is = state.launchedAt ?? {};
    seenLaunch.current = is;
    if (mock) return;
    const ran = Object.keys(is).find((id) => (is[id] ?? 0) > (was[id] ?? 0) && splitOrder(order).members.includes(id));
    if (ran) setLaunch((l) => ({ seq: l.seq + 1, to: ran }));
    // Only a new launchedAt is news; the order is read as it stands then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.launchedAt, mock]);

  const label = LAUNCH_ACCOUNT_AGENT_LABEL[agent];
  const { members, others } = splitOrder(order);
  const lanes = poolLanes(state, accounts, members, { now, usage: scheduler?.usage ?? [], meta: (a) => accountMeta(state, a), others });
  const plan = poolPlan(lanes, strategy, state.launchedAt);
  const dirty = JSON.stringify({ members, strategy, whenUnset }) !== JSON.stringify({ members: prev?.members ?? [], strategy: prev?.strategy ?? 'in-order', whenUnset: prev?.whenUnset ?? true });
  const unknown = strategy === 'spread' ? lanes.filter((l) => l.inPool && l.canRun && l.headroom === null).length : 0;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await savePool(agent, { members, strategy, whenUnset });
      toast.success(members.length ? 'Pool saved' : 'Pool removed');
      onClose();
    } catch (e) {
      setError(toUiError(e, { what: 'The pool was not saved.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      eyebrow={label}
      title="Pool"
      description="A running session never moves to another account."
      width={480}
      resizeKey={null}
      bodyClassName="flex flex-col gap-6 px-6 py-4"
      footer={
        <Button variant="primary" className="ml-auto" loading={busy} disabled={!dirty} onClick={() => void save()}>
          Save pool
        </Button>
      }
    >
      <section className="flex flex-col gap-3" aria-labelledby="pool-how">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h3 id="pool-how" className="text-ui font-medium text-fg">
            How launches land
          </h3>
          <Segmented label="How Orgus picks" size="sm" options={STRATEGY_OPTIONS} value={strategy} onChange={setStrategy} className="ml-auto" />
        </div>
        <p className="text-caption text-fg-subtle">
          {strategy === 'in-order'
            ? 'The first account that can run now, top to bottom.'
            : unknown
              ? 'Most go where the most headroom is left; an account with no reading counts as full.'
              : 'Most go where the most headroom is left in its usage windows.'}
        </p>
        <PoolChannel lanes={lanes} plan={plan} strategy={strategy} now={now} onOrder={setOrder} launch={launch} onAdd={onAdd} loading={phase === 'loading'} />
      </section>
      <UnnamedLaunch on={whenUnset} onChange={setWhenUnset} />
      {error && <ErrorState variant="inline" error={error} />}
    </Drawer>
  );
}

// ── The saved accounts ───────────────────────────────────────────────────────

/** One row per saved account of every agent: what it is, how it connects, its machines, one status. */
function AccountList({ state, accounts, label, onOpen }: { state: LaunchAccountsState; accounts: LaunchAccount[]; label: string; onOpen: (id: string) => void }) {
  const machines = useMachines();
  return (
    <RowList label={label}>
      {accounts.map((a) => {
        const st = accountStatus(state, a, machines);
        const chips = a.kind === 'subscription' ? machineChips(state, a, machines) : [];
        return (
          <ListRow
            key={a.id}
            lead={
              <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-control bg-hover">
                <AgentGlyph kind={launchAccountAgent(a)} className="size-3.5" />
              </span>
            }
            onOpen={() => onOpen(a.id)}
            trail={
              <span className="flex items-center gap-3">
                <UsageAtAGlance a={a} />
                {chips.length > 1 ? (
                  <MachineDots chips={chips} className="hidden sm:flex" />
                ) : a.fallback ? (
                  <Badge tone="neutral" variant="outline" data-chip="" title="Takes over when a subscription runs out">
                    Fallback
                  </Badge>
                ) : null}
                <RowStatus {...st} hideLabel="below-lg" />
              </span>
            }
            multiline
            aria-label={`${a.name}, ${st.label}${a.fallback ? ', fallback' : ''}`}
          >
            <RowTitle title={a.name} meta={accountMeta(state, a)} />
          </ListRow>
        );
      })}
    </RowList>
  );
}

/** Each agent's pool, as one row per agent that has two accounts or more to pick between. */
function PoolRows({ state, onPool }: { state: LaunchAccountsState; onPool: (agent: LaunchAccountAgent) => void }) {
  const agents = SHOWN_AGENTS.filter((g) => accountsOf(state, g).length >= 2);
  if (!agents.length) return <p className="px-2 text-caption text-fg-subtle">A pool needs a second account of the same agent.</p>;
  return (
    <RowList label="Pools">
      {agents.map((g) => {
        const pool = poolStatus(state, g);
        const label = LAUNCH_ACCOUNT_AGENT_LABEL[g];
        return (
          <ListRow key={g} lead={<AgentGlyph kind={g} className="size-4" />} onOpen={() => onPool(g)} trail={<RowStatus {...pool} hideLabel="below-lg" />} aria-label={`${label} pool, ${pool.label}`}>
            <RowTitle title={`${label} pool`} />
          </ListRow>
        );
      })}
    </RowList>
  );
}

// ── Project defaults ─────────────────────────────────────────────────────────

function ProjectDefaults({ state }: { state: LaunchAccountsState }) {
  const projects = useProjects();
  const [error, setError] = useState<UiError | null>(null);
  // A column only for an agent with an account of its own to choose.
  const agents = SHOWN_AGENTS.filter((g) => accountsOf(state, g).some((a) => !a.builtIn));
  if (!projects.length || !agents.length) return null;
  const optionsOf = (agent: LaunchAccountAgent) => {
    const icon = <AgentGlyph kind={agent} className="size-3.5" />;
    return [
      { value: '', label: 'No default', icon },
      ...accountsOf(state, agent).map((a) => ({
        value: a.id,
        label: a.name,
        icon,
      })),
      ...(state.pools?.[agent]
        ? [
            {
              value: POOL_LAUNCH_ACCOUNT_ID,
              label: POOL_LAUNCH_ACCOUNT_LABEL,
              icon,
            },
          ]
        : []),
    ];
  };
  const change = async (projectId: string, agent: LaunchAccountAgent, accountId: string) => {
    setError(null);
    try {
      await setProjectDefault(projectId, accountId || null, agent);
    } catch (e) {
      setError(toUiError(e, { what: 'The default account was not saved.' }));
    }
  };
  return (
    <Panel title="Project defaults" sub="When a launch names no account" flush>
      <RowList label="Project defaults">
        {projects.map((p) => (
          <ListRow
            key={p.id}
            trail={
              <span className="flex shrink-0 gap-2">
                {agents.map((g) => (
                  <Select
                    key={g}
                    aria-label={`Default ${LAUNCH_ACCOUNT_AGENT_LABEL[g]} account for ${p.name}`}
                    size="sm"
                    wrapperClassName="w-32 sm:w-44"
                    value={state.defaults[p.id]?.[g] ?? ''}
                    onValueChange={(v) => void change(p.id, g, v)}
                    options={optionsOf(g)}
                  />
                ))}
              </span>
            }
          >
            <RowTitle title={p.name} />
          </ListRow>
        ))}
      </RowList>
      {error && <ErrorState variant="inline" error={error} className="m-3" />}
    </Panel>
  );
}

// ── The section ──────────────────────────────────────────────────────────────

export function LaunchAccountsView() {
  const { phase, state, mock } = useLaunchAccounts();
  const loadError = useOrgus((s) => s.loadError);
  const write = useCan('operator');
  const [connecting, setConnecting] = useState<{
    agent: LaunchAccountAgent | null;
  } | null>(null);
  const [opened, setOpened] = useState<{
    id: string;
    machineId: string | null;
  } | null>(null);
  const [poolAgent, setPoolAgent] = useState<LaunchAccountAgent | null>(null);
  const [signIn, setSignIn] = useState<(OpenedSignIn & { accountId: string }) | null>(null);

  if (phase === 'loading')
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Loading accounts">
        <div className="grid gap-3 md:grid-cols-3">
          {SHOWN_AGENTS.map((g) => (
            <Skeleton key={g} className="h-40 rounded-card" />
          ))}
        </div>
        <SkeletonRows rows={3} />
      </div>
    );
  if (phase === 'error') return <ErrorState error={loadError ?? new Error('The hub could not be reached.')} />;
  if (phase === 'missing' || !state)
    return (
      <ModuleAbsent
        icon={<KeyRound />}
        feature="Agent accounts"
        plural
        hub={{
          title: 'Accounts are not available on this hub',
          description: 'Update Orgus on the hub machine.',
        }}
        instead="Until then, each machine runs its agents with the sign-in it already has."
      />
    );

  const open = opened ? (state.accounts.find((a) => a.id === opened.id) ?? null) : null;
  const own = state.accounts.filter((a) => !a.builtIn);
  const builtIns = state.accounts.filter((a) => a.builtIn);
  const projects = Object.keys(state.defaults).length;
  const pools = SHOWN_AGENTS.filter((g) => state.pools?.[g]).length;
  return (
    <div className="flex flex-col gap-6">
      {mock && <p className="text-caption text-fg-subtle">Demo data</p>}
      <AgentTiles state={state} onConnect={(g) => setConnecting({ agent: g })} onOpen={(id, machineId) => setOpened({ id, machineId: machineId ?? null })} />
      {own.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Accounts">
          <div className="flex items-center gap-3">
            <h3 className="min-w-0 flex-1 text-ui font-medium text-fg">Accounts</h3>
            {own.some((a) => launchAccountAgent(a) === 'claude-code') && (
              <>
                <TestAllButton accounts={accountsOf(state, 'claude-code')} />
                <RefreshUsageButton state={state} />
              </>
            )}
            <Button size="sm" variant="ghost" icon={<Plus />} disabled={!write} onClick={() => setConnecting({ agent: null })}>
              Connect account
            </Button>
          </div>
          <div className="rounded-card bg-surface">
            <AccountList state={state} accounts={own} label="Accounts" onOpen={(id) => setOpened({ id, machineId: null })} />
          </div>
        </section>
      )}
      <section className="rounded-card bg-surface px-2" aria-label="Advanced">
        <Disclosure title="Advanced" summary={[pools ? plural(pools, 'pool') : null, projects ? plural(projects, 'project default') : null].filter(Boolean).join(', ') || "Machines' own sign-in, pools, project defaults"}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h4 className="px-2 text-ui font-medium text-fg">Each machine's own sign-in</h4>
              <p className="px-2 text-caption text-fg-subtle">Sessions with no account run on whatever each machine already signed in to.</p>
              <AccountList state={state} accounts={builtIns} label="Machines' own sign-in" onOpen={(id) => setOpened({ id, machineId: null })} />
            </div>
            <div className="flex flex-col gap-2">
              <h4 className="px-2 text-ui font-medium text-fg">Pools</h4>
              <p className="px-2 text-caption text-fg-subtle">Orgus picks an account at launch and passes over one that is over its limit.</p>
              <PoolRows state={state} onPool={setPoolAgent} />
            </div>
            {own.length > 0 && (
              <div className="flex flex-col gap-2">
                <h4 className="px-2 text-ui font-medium text-fg">Project defaults</h4>
                <p className="px-2 text-caption text-fg-subtle">The account a project's sessions use when a launch names none.</p>
                <ProjectDefaults state={state} />
              </div>
            )}
          </div>
        </Disclosure>
      </section>
      <ConnectDialog open={connecting !== null} agent={connecting?.agent ?? null} onOpenChange={(o) => !o && setConnecting(null)} onOpenAccount={(id) => setOpened({ id, machineId: null })} />
      <AccountDrawer
        state={state}
        account={open}
        machineId={opened?.machineId ?? null}
        signIn={open && signIn?.accountId === open.id ? signIn : null}
        onSignIn={(s) => setSignIn(open && s ? { accountId: open.id, ...s } : null)}
        onClose={() => setOpened(null)}
      />
      <PoolDrawer state={state} agent={poolAgent} onClose={() => setPoolAgent(null)} onAdd={(g) => setConnecting({ agent: g })} />
    </div>
  );
}
